import { Controller } from "@hotwired/stimulus"

// An address carries the one account it belongs to, a card brand carries every account that
// supports it as a space separated list, so the two are matched differently.
const addressSupported = (accounts, issuingAccount) => accounts === issuingAccount
const cardBrandSupported = (accounts, issuingAccount) => accounts.split(' ').includes(issuingAccount)

export default class extends Controller {
  static targets = [ "maxTesters", "budget", "cardLimit", "stripeAccount", "address", "cardBrand", "taskCardBrand" ]

  connect() {
    this.originalAddressOptions = Array.from(this.addressTarget.children).map(option => ({
      value: option.value,
      text: option.textContent,
      issuingAccount: option.dataset.issuingAccount,
    }));

    this.originalCardBrandOptions = Array.from(this.cardBrandTarget.children).map(option => ({
      value: option.value,
      text: option.textContent,
      issuingAccount: option.dataset.issuingAccount,
    }));

    this.#dropUnsupportedOptions();

    // Tom Select initializes after this controller, so its instances only exist next frame.
    requestAnimationFrame(() => this.#forgetUnsupportedOnRemoval());
  }

  // updateAddresses/updateCardBrands only run when the Stripe account changes, so a page that
  // *loads* with an account already set — edit, duplicate, or the re-render after a validation
  // error — would otherwise offer values the account does not support. Tom Select has not
  // initialized yet at this point, so prune the raw <option> elements and let it start from the
  // right set. Already selected values are kept, so a rejected one stays visible next to the
  // error explaining it rather than silently disappearing.
  #dropUnsupportedOptions() {
    const issuingAccount = this.issuingAccount;

    if (!issuingAccount) return;

    this.#pruneOptions(this.addressTarget, accounts => addressSupported(accounts, issuingAccount));
    this.#pruneOptions(this.cardBrandTarget, accounts => cardBrandSupported(accounts, issuingAccount));
  }

  #pruneOptions(select, supported) {
    Array.from(select.options).forEach(option => {
      const accounts = option.dataset.issuingAccount;

      if (option.selected || !option.value || !accounts) return;
      if (!supported(accounts)) option.remove();
    });
  }

  // Once the user deselects a value the account does not support, it must not drop back into the
  // list — otherwise they can pick it straight back and hit the same validation error in a loop.
  #forgetUnsupportedOnRemoval() {
    this.#forgetUnsupported(this.addressTarget, this.originalAddressOptions, addressSupported);
    this.#forgetUnsupported(this.cardBrandTarget, this.originalCardBrandOptions, cardBrandSupported);
  }

  #forgetUnsupported(select, snapshot, supported) {
    const instance = select.tomselect;

    if (!instance) return;

    instance.on('item_remove', value => {
      const issuingAccount = this.issuingAccount;
      const accounts = snapshot.find(option => option.value === value)?.issuingAccount;

      if (!issuingAccount || !accounts) return;
      if (!supported(accounts, issuingAccount)) instance.removeOption(value);
    });
  }

  updateBudget(event) {
    const cardLimit = Number(this.cardLimitTarget.value)
    const maxTesters = Number(this.maxTestersTarget.value)

    if (cardLimit == 0 || maxTesters == 0) {
      this.budgetTarget.value = "N/A"
    } else {
      this.budgetTarget.value = (cardLimit * maxTesters).toFixed(2)
    }
  }

  updateAddresses() {
    const issuingAccount = this.issuingAccount;
    const selectEl = this.addressTarget;

    if (!selectEl || !this.originalAddressOptions) return;

    const tomSelectInstance = selectEl.tomselect;

    if (tomSelectInstance) {
      tomSelectInstance.clearOptions();

      this.originalAddressOptions.forEach(optionData => {
        const account = optionData.issuingAccount;
        const matches = !issuingAccount || !account || addressSupported(account, issuingAccount);

        if (matches && optionData.value) {
          tomSelectInstance.addOption({
            value: optionData.value,
            text: optionData.text
          });
        }
      });

      tomSelectInstance.refreshOptions(false);
    }
  }

  updateCardBrands() {
    const issuingAccount = this.issuingAccount;
    const selectEl = this.cardBrandTarget;

    if (!selectEl || !this.originalCardBrandOptions) return;

    const tomSelectInstance = selectEl.tomselect;

    if (tomSelectInstance) {
      tomSelectInstance.clearOptions();

      this.originalCardBrandOptions.forEach(optionData => {
        const accounts = optionData.issuingAccount || "";
        const matches = !issuingAccount || !accounts || cardBrandSupported(accounts, issuingAccount);

        if (matches && optionData.value) {
          tomSelectInstance.addOption({
            value: optionData.value,
            text: optionData.text
          });
        }
      });

      tomSelectInstance.refreshOptions(false);
    }
  }

  updateTaskCardBrands() {
    this.taskCardBrandTargets.forEach(select => this.updateSingleTaskCardBrand(select))
  }

  taskCardBrandTargetConnected(element) {
    this.updateSingleTaskCardBrand(element)
  }

  updateSingleTaskCardBrand(select) {
    const selectedBrands = this.selectedCardBrands
    const brandLabels = { mastercard: 'Mastercard', visa: 'Visa' }
    const currentValue = select.value

    select.innerHTML = ''

    selectedBrands.forEach(brand => {
      const option = document.createElement('option')
      option.value = brand
      option.textContent = brandLabels[brand] || brand
      select.appendChild(option)
    })

    const noneOption = document.createElement('option')
    noneOption.value = ''
    noneOption.textContent = 'None'
    select.appendChild(noneOption)

    if (selectedBrands.includes(currentValue)) {
      select.value = currentValue
    } else {
      select.value = ''
    }
  }

  get selectedCardBrands() {
    const selectEl = this.cardBrandTarget
    const tomSelectInstance = selectEl.tomselect
    if (tomSelectInstance) {
      return tomSelectInstance.getValue()
    }
    return Array.from(selectEl.selectedOptions).map(o => o.value).filter(Boolean)
  }

  get issuingAccount() {
    if (this.stripeAccountTarget.tagName === 'SELECT') {
      const selectedOption = this.stripeAccountTarget.selectedOptions[0]
      return selectedOption.dataset.issuingAccount || selectedOption.value
    } else {
      return this.stripeAccountTarget.value
    }
  }
}
